import React, { useMemo } from "react";
import { SquarePen } from "lucide-react";

import { useAppSelector } from "../../../app/store";
import { FormFieldType, FormType } from "../../../api/formConfigs";
import { selectFormConfigById } from "../Logic/formConfigsSlice";

import DocumentDetailsFieldBlock from "./DocumentDetailsFieldBlock";

interface ConfigBasedFormHoverCardProps {
  title?: string;
  formType: FormType;
  values?: Record<string, any>;
  onEdit(): void;
}

const ConfigBasedFormHoverCard: React.FC<ConfigBasedFormHoverCardProps> = ({
  title = "Pre-Login State",
  formType,
  values,
  onEdit,
}) => {
  const formConfigData = useAppSelector((state) =>
    selectFormConfigById(state, formType)
  );

  const { configs } = formConfigData ?? {};

  const orderedFields = useMemo(() => {
    if (!configs?.fields) return [];

    const leaderKeys = configs.leader_field_keys ?? [];
    const leaderFields = configs.fields.filter((field) =>
      leaderKeys.includes(field.key)
    );
    const remainingFields = configs.fields.filter(
      (field) => !leaderKeys.includes(field.key)
    );

    return [...leaderFields, ...remainingFields];
  }, [configs]);

  const fieldSpanClasses = useMemo(() => {
    const spans: Record<string, boolean> = {};
    let currentColumn = 0;

    for (let i = 0; i < orderedFields.length; i++) {
      const field = orderedFields[i];
      if (!field) continue;

      const isWideType = field.field_type === FormFieldType.TEXT_AREA;

      if (isWideType) {
        if (currentColumn === 1 && i > 0) {
          const prevField = orderedFields[i - 1];
          spans[prevField.key] = true;
        }
        spans[field.key] = true;
        currentColumn = 0;
      } else {
        spans[field.key] = false;
        currentColumn = (currentColumn + 1) % 2;
      }
    }

    if (currentColumn === 1 && orderedFields.length > 0) {
      const lastStandardField = [...orderedFields]
        .reverse()
        .find((f) => f && !spans[f.key]);
      if (lastStandardField) {
        spans[lastStandardField.key] = true;
      }
    }

    return spans;
  }, [orderedFields]);

  if (!formConfigData || !configs) {
    return null;
  }

  return (
    <div className="w-[358px] rounded-[8px] border border-[#E5E5E5] bg-white p-3 shadow-md flex flex-col gap-2 items-start">
      <div className="flex items-center justify-between w-full">
        <span className="text-[12px] font-semibold leading-4 text-[#737373]">
          {title}
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-2 text-[12px] font-medium leading-5 text-[#3551F3] focus:outline-none"
        >
          <SquarePen size={16} />
          Edit
        </button>
      </div>

      <div className="max-h-[260px] overflow-y-auto w-full">
        <div className="grid grid-cols-2 gap-2 w-full">
          {orderedFields.map((field) => {
            if (!field) return null;

            const value = values?.[field.key];
            const isWide = fieldSpanClasses[field.key] ?? false;

            return (
              <div key={field.key} className={isWide ? "col-span-2" : "col-span-1"}>
                <DocumentDetailsFieldBlock
                  field={field}
                  value={value}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConfigBasedFormHoverCard;